<template>
  <div v-if="$can('view-comments', 'Yacht')">
    <v-row
      no-gutters
      justify="center"
    >
      <v-col cols="12">
        <validation-observer ref="validationObserver">
          <validation-provider
            v-slot="{ errors }"
            rules="required"
            name="comment"
          >
            <v-card
              class="pa-0"
              rounded
            >
              <div class="editor">
                <input
                  v-model="strippedComment"
                  type="hidden"
                >
                <editor-menu-bar
                  v-slot="{ commands, isActive }"
                  :editor="editor"
                >
                  <div class="grey lighten-2">
                    <v-btn-toggle
                      dense
                      group
                      borderless
                      background-color="transparent"
                    >
                      <v-btn
                        icon
                        :input-value="isActive.bold()"
                        @click="commands.bold()"
                      >
                        <v-icon>
                          format_bold
                        </v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        :input-value="isActive.italic()"
                        @click="commands.italic()"
                      >
                        <v-icon>
                          format_italic
                        </v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        :input-value="isActive.underline()"
                        @click="commands.underline()"
                      >
                        <v-icon>
                          format_underline
                        </v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        :input-value="isActive.strike()"
                        @click="commands.strike()"
                      >
                        <v-icon>
                          format_strikethrough
                        </v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        :input-value="isActive.blockquote()"
                        @click="commands.blockquote()"
                      >
                        <v-icon>
                          format_quote
                        </v-icon>
                      </v-btn>
                      <v-divider vertical />
                      <v-btn
                        icon
                        :input-value="isActive.bullet_list()"
                        @click="commands.bullet_list()"
                      >
                        <v-icon>
                          format_list_bulleted
                        </v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        :input-value="isActive.ordered_list()"
                        @click="commands.ordered_list()"
                      >
                        <v-icon>
                          format_list_numbered
                        </v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        :input-value="isActive.code_block()"
                        @click="commands.code_block()"
                      >
                        <v-icon>
                          code
                        </v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        :input-value="isActive.horizontal_rule()"
                        @click="commands.horizontal_rule()"
                      >
                        <v-icon>
                          horizontal_rule
                        </v-icon>
                      </v-btn>
                    </v-btn-toggle>
                  </div>
                </editor-menu-bar>
                <editor-menu-bubble
                  v-slot="{ commands, isActive, getMarkAttrs, menu }"
                  class="menububble"
                  :editor="editor"
                  @hide="hideLinkMenu"
                >
                  <div
                    class="menububble"
                    :class="{ 'is-active': menu.isActive }"
                    :style="`left: ${menu.left}px; bottom: ${menu.bottom}px;`"
                  >
                    <form
                      v-if="linkMenuIsActive"
                      class="menububble__form"
                      @submit.prevent="setLinkUrl(commands.link, linkUrl)"
                    >
                      <input
                        ref="linkInput"
                        v-model="linkUrl"
                        class="menububble__input"
                        type="text"
                        placeholder="https://"
                        @keydown.esc="hideLinkMenu"
                      >
                      <button
                        class="menububble__button"
                        type="button"
                        @click="setLinkUrl(commands.link, null)"
                      >
                        <v-icon
                          dark
                          right
                        >
                          delete
                        </v-icon>
                      </button>
                    </form>

                    <template v-else>
                      <button
                        class="menububble__button"
                        :class="{ 'is-active': isActive.link() }"
                        @click="showLinkMenu(getMarkAttrs('link'))"
                      >
                        <span>{{ isActive.link() ? 'Update Link' : 'Add Link' }}</span>
                        <v-icon
                          dark
                          right
                        >
                          link
                        </v-icon>
                      </button>
                    </template>
                  </div>
                </editor-menu-bubble>
                <editor-content
                  class="editor__content"
                  :editor="editor"
                />
              </div>

              <div
                v-show="showSuggestions"
                ref="suggestions"
                class="suggestion-list"
              >
                <template v-if="hasResults">
                  <div
                    v-for="(user, index) in filteredUsers"
                    :key="user.type + '_' + user.id"
                    class="suggestion-list__item"
                    :class="{ 'is-selected': navigatedUserIndex === index }"
                    @click="selectUser(user)"
                  >
                    {{ user.name }}
                  </div>
                </template>
                <div
                  v-else
                  class="suggestion-list__item is-empty"
                >
                  No users found
                </div>
              </div>
            </v-card>
            <div
              v-if="errors.length > 0"
              class="caption error--text"
            >
              {{ errors[0] }}
            </div>
          </validation-provider>
          <v-row
            no-gutters
            align="center"
            class="mt-2"
          >
            <v-col cols="7">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ required: !newComment.visibleAll }"
              >
                <v-autocomplete
                  v-model="newComment.visibleTo"
                  :items="allMentionable"
                  return-object
                  :error-messages="errors"
                  item-text="name"
                  :item-value="(item) => item.type + '_' + item.id"
                  label="Select roles/people comment is visible to"
                  outlined
                  multiple
                  :disabled="!!newComment.visibleAll"
                  small-chips
                  background-color="white"
                  class="elevation-1"
                  deletable-chips
                  hide-details
                  clearable
                />
              </validation-provider>
            </v-col>
            <v-col
              cols="auto"
              class="pl-3"
            >
              <v-switch
                v-model="newComment.visibleAll"
                label="Visible to all"
                dense
                hide-details
                class="pa-0 ma-0"
              />
            </v-col>
            <v-col
              cols="auto"
              class="pl-3"
            >
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <div v-on="on">
                    <v-switch
                      v-model="newComment.notify"
                      :disabled="!!newComment.id || notifyTo.length === 0"
                      label="Notify tagged by email"
                      dense
                      hide-details
                      class="pa-0 ma-0"
                    >
                      <template v-slot:label>
                        Notify tagged by email
                        <v-icon
                          small
                          class="ml-1"
                        >
                          help
                        </v-icon>
                      </template>
                    </v-switch>
                  </div>
                </template>
                <span>
                  Tag people or roles with @ to send them notifications.
                </span>
              </v-tooltip>
            </v-col>
            <v-col cols="auto" />
            <v-col
              cols="auto"
              class="pl-3"
            >
              <v-switch
                v-model="newComment.pinned"
                label="Pinned"
                hide-details
                dense
                class="pa-0 ma-0"
              />
            </v-col>
            <v-spacer />
            <v-col cols="auto">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    color="primary"
                    :loading="newComment.submitting"
                    @click="submit()"
                    v-on="notifyTo.length > 0 && newComment.notify ? on : undefined"
                  >
                    {{ newComment.id ? 'Save' : 'Send' }}
                  </v-btn>
                </template>
                <div v-if="notifyTo.length > 0 && newComment.notify">
                  <div>Email notification will be sent to:</div>
                  <ul>
                    <li
                      v-for="item in notifyTo"
                      :key="'notification_to_'+item.id"
                    >
                      {{ item.type === 'role' ? `All of ${item.name}` : item.name }}
                    </li>
                  </ul>
                </div>
              </v-tooltip>
            </v-col>
          </v-row>
        </validation-observer>
        <v-divider class="my-4" />
        <div v-if="!comments.loading">
          <div
            v-if="comments.data.length > 0"
            class="mb-16"
          >
            <comment-row
              v-for="comment in comments.data"
              :key="comment.id"
              :comment="comment"
              class="mb-4"
              @comment:deleted="onCommentDeleted(comment.id)"
              @comment:updated="onCommentUpdated"
              @comment:edit="initCommentEdit"
            />
          </div>
          <div
            v-else
            class="text-center title grey--text mt-10"
          >
            <div>
              <v-icon
                x-large
                color="grey lighten-2"
              >
                comment
              </v-icon>
            </div>
            <div class="mt-3">
              There are no comments yet
            </div>
          </div>
        </div>
        <div v-else>
          <v-row
            v-for="index in 5"
            :key="index"
            no-gutters
          >
            <v-col
              cols="auto"
              class="pr-4"
            >
              <v-skeleton-loader
                type="avatar"
                height="50px"
              />
            </v-col>
            <v-col>
              <v-skeleton-loader
                type="text"
                width="200px"
                class="my-2"
              />
              <v-skeleton-loader
                class="mt-2"
                type="paragraph"
              />
            </v-col>
          </v-row>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import Fuse from 'fuse.js'
import tippy, { sticky } from 'tippy.js'
import { Editor, EditorContent, EditorMenuBar, EditorMenuBubble, Extension } from 'tiptap'
import {
  Blockquote,
  Bold,
  BulletList,
  Code,
  CodeBlock,
  HardBreak,
  History,
  HorizontalRule,
  Italic,
  Link,
  ListItem,
  Mention,
  OrderedList,
  Placeholder,
  Strike,
  Underline
} from 'tiptap-extensions'
import Role from '@/models/Role'
import Comments from '@/services/api/Comments'
import CommentRow from '@/components/business/yachts/CommentRow'

export default {
  name: 'YachtComments',
  components: {
    CommentRow,
    // eslint-disable-next-line vue/no-unused-components
    EditorContent,
    EditorMenuBar,
    EditorMenuBubble
  },
  props: {
    yacht: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    const vm = this

    return {
      comments: {
        loading: false,
        data: []
      },
      roles: [],
      newComment: {
        id: undefined,
        content: '',
        visibleAll: false,
        visibleTo: [],
        notify: false,
        pinned: false,
        submitting: false
      },
      editor: new Editor({
        onUpdate: ({ getHTML }) => {
          this.newComment.content = getHTML()
        },
        extensions: [
          new HorizontalRule(),
          new Underline(),
          new Strike(),
          new BulletList(),
          new OrderedList(),
          new ListItem(),
          new HardBreak(),
          new Blockquote(),
          new History(),
          new Link(),
          new Placeholder({
            emptyNodeText: 'Write your comment or note here...'
          }),
          new Mention({
            // a list of all suggested items
            items: () => this.allMentionable,
            // is called when a suggestion starts
            onEnter: ({ items, query, range, command, virtualNode }) => {
              this.query = query
              this.filteredUsers = items
              this.suggestionRange = range
              this.renderPopup(virtualNode)
              // we save the command for inserting a selected mention
              // this allows us to call it inside of our custom popup
              // via keyboard navigation and on click
              this.insertMention = command
            },
            // is called when a suggestion has changed
            onChange: ({ items, query, range, virtualNode }) => {
              this.query = query
              this.filteredUsers = items
              this.suggestionRange = range
              this.navigatedUserIndex = 0
              this.renderPopup(virtualNode)
            },
            // is called when a suggestion is cancelled
            onExit: () => {
              // reset all saved values
              this.query = null
              this.filteredUsers = []
              this.suggestionRange = null
              this.navigatedUserIndex = 0
              this.destroyPopup()
            },
            // is called on every keyDown event while a suggestion is active
            onKeyDown: ({ event }) => {
              if (event.key === 'ArrowUp') {
                this.upHandler()
                return true
              }
              if (event.key === 'ArrowDown') {
                this.downHandler()
                return true
              }
              if (event.key === 'Enter') {
                this.enterHandler()
                return true
              }
              return false
            },
            // is called when a suggestion has changed
            // this function is optional because there is basic filtering built-in
            // you can overwrite it if you prefer your own filtering
            // in this example we use fuse.js with support for fuzzy search
            onFilter: async (items, query) => {
              if (!query) {
                return items
              }
              const fuse = new Fuse(items, {
                threshold: 0.2,
                keys: ['name']
              })
              return fuse.search(query).map(item => item.item)
            }
          }),
          new Code(),
          new CodeBlock(),
          new Bold(),
          new Italic(),
          new (class extends Extension {
            keys(what) {
              return {
                'Ctrl-Enter': () => {
                  vm.submit()
                }
              }
            }
          })()
        ],
        content: ''
      }),
      query: null,
      suggestionRange: null,
      filteredUsers: [],
      navigatedUserIndex: 0,
      insertMention: () => {},
      linkUrl: null,
      linkMenuIsActive: false
    }
  },
  computed: {
    hasResults() {
      return this.filteredUsers.length
    },
    showSuggestions() {
      return this.query || this.hasResults
    },
    allMentionable() {
      let all = []

      for (let role of this.roles) {
        all.push({ id: role.id, type: 'role', name: role.title })
      }

      for (let staff of this.$store.state.service.staff.data) {
        all.push({ id: staff.id, type: 'staff', name: staff.name })
      }

      return all
    },
    strippedComment() {
      return this.newComment.content.replace(/<[^>]+>/g, '')
    },
    notifyTo() {
      const doc = this.domparser.parseFromString(this.newComment.content, 'text/html')

      const notifyNames = Array.from(doc.getElementsByClassName('mention')).map(i => {
        return i.textContent.replace('@', '').trim()
      })

      return this.allMentionable.filter(item => {
        return notifyNames.indexOf(item.name) !== -1
      })
    }
  },
  created() {
    this.fetchRoles()
    this.fetchComments()

    this.domparser = new DOMParser()
  },
  beforeDestroy() {
    this.destroyPopup()
  },
  methods: {
    submit: async function () {
      if (!(await this.$refs.validationObserver.validate())) return

      await this.save()
    },
    save: async function () {
      this.newComment.submitting = true

      try {
        const comment = await Comments.save({
          commentable_type: 'App\\Yacht',
          commentable_id: this.yacht.id,
          comment: this.newComment.content,
          visibility: this.newComment.visibleAll ? null : this.newComment.visibleTo,
          pinned: this.newComment.pinned,
          notify: this.newComment.notify ? this.notifyTo.map(i => ({ id: i.id, type: i.type })) : null
        }, this.newComment.id)

        if (this.newComment.id) {
          this.onCommentUpdated(comment)
        } else {
          this.comments.data.unshift(comment)
        }

        this.newComment = {
          id: undefined,
          content: '',
          visibleAll: false,
          visibleTo: [],
          notify: false,
          pinned: false,
          submitting: false
        }

        this.editor.clearContent()

        this.$refs.validationObserver.reset()
      } catch (e) {
        throw e
      } finally {
        this.newComment.submitting = false
      }
    },
    fetchComments: async function() {
      try {
        this.comments.loading = true

        this.comments.data = await Comments.fetch({ commentable_type: 'App\\Yacht', commentable_id: this.yacht.id })
      } catch (e) {
        throw e
      } finally {
        this.comments.loading = false
      }
    },
    fetchRoles: async function() {
      try {
        this.roles = await Role.api.fetch()
      } catch (e) {
        this.$store.dispatch('alert', {
          'text': 'Something went wrong getting roles',
          'color': 'error'
        })
        throw e
      }
    },
    // navigate to the previous item
    // if it's the first item, navigate to the last one
    upHandler() {
      this.navigatedUserIndex = ((this.navigatedUserIndex + this.filteredUsers.length) - 1) % this.filteredUsers.length
    },
    // navigate to the next item
    // if it's the last item, navigate to the first one
    downHandler() {
      this.navigatedUserIndex = (this.navigatedUserIndex + 1) % this.filteredUsers.length
    },
    enterHandler() {
      const user = this.filteredUsers[this.navigatedUserIndex]
      if (user) {
        this.selectUser(user)
      }
    },
    // we have to replace our suggestion text with a mention
    // so it's important to pass also the position of your suggestion text
    selectUser(user) {
      this.insertMention({
        range: this.suggestionRange,
        attrs: {
          id: user.id,
          label: user.name + ' '
        }
      })
      this.editor.focus()

      if (!this.newComment.visibleTo.some(u => u.id === user.id && u.type === user.type)) {
        this.newComment.visibleTo.push(user)
        this.newComment.notify = true
      }
    },
    // renders a popup with suggestions
    // tiptap provides a virtualNode object for using popper.js (or tippy.js) for popups
    renderPopup(node) {
      const boundingClientRect = node.getBoundingClientRect()
      const { x, y } = boundingClientRect
      if (x === 0 && y === 0) {
        return
      }
      if (this.popup) {
        return
      }
      // ref: https://atomiks.github.io/tippyjs/v6/all-props/
      this.popup = tippy('#app', {
        getReferenceClientRect: () => boundingClientRect,
        appendTo: () => document.body.getElementsByClassName('v-application')[0],
        interactive: true,
        sticky: true, // make sure position of tippy is updated when content changes
        plugins: [sticky],
        content: this.$refs.suggestions,
        trigger: 'mouseenter', // manual
        showOnCreate: true,
        theme: 'dark',
        placement: 'top-start',
        inertia: true,
        duration: [400, 200]
      })
    },
    destroyPopup() {
      if (this.popup) {
        this.popup[0].destroy()
        this.popup = null
      }
    },
    showLinkMenu(attrs) {
      this.linkUrl = attrs.href
      this.linkMenuIsActive = true
      this.$nextTick(() => {
        this.$refs.linkInput.focus()
      })
    },
    hideLinkMenu() {
      this.linkUrl = null
      this.linkMenuIsActive = false
    },
    setLinkUrl(command, url) {
      if (!url.startsWith('http')) url = 'http://' + url

      command({ href: url })
      this.hideLinkMenu()
    },
    onCommentDeleted(id) {
      let index = this.comments.data.findIndex(c => c.id === id)

      if (index === -1) return

      this.comments.data.splice(index, 1)
    },
    onCommentUpdated(comment) {
      let index = this.comments.data.findIndex(c => c.id === comment.id)

      if (index === -1) return

      this.comments.data.splice(index, 1, comment)
    },
    initCommentEdit(comment) {
      this.newComment.id = comment.id
      this.newComment.content = comment.comment

      this.editor.setContent(comment.comment)

      this.newComment.visibleAll = comment.is_public
      this.newComment.pinned = comment.is_pinned
      this.newComment.notify = false

      if (!comment.is_public) {
        this.newComment.visibleTo = [
          ...comment.visible_staff.map(staff => ({ id: staff.id, type: 'staff', name: staff.name })),
          ...comment.visible_roles.map(role => ({ id: role.id, type: 'role', name: role.title }))
        ]
      } else {
        this.newComment.visibleTo = []
      }

      this.$vuetify.goTo(0)
    }
  }
}
</script>

<style lang="scss">
$color-black: #000000;
$color-white: #ffffff;

.mention {
  background: rgba($color-black, 0.1);
  color: rgba($color-black, 0.6);
  font-size: 0.8rem;
  font-weight: bold;
  border-radius: 5px;
  padding: 0.2rem 0.5rem;
  white-space: nowrap;
}
.mention-suggestion {
  color: rgba($color-black, 0.6);
}
.suggestion-list {
  padding: 0.2rem;
  border: 2px solid rgba($color-black, 0.1);
  font-size: 0.8rem;
  font-weight: bold;
  &__no-results {
    padding: 0.2rem 0.5rem;
  }
  &__item {
    border-radius: 5px;
    padding: 0.2rem 0.5rem;
    margin-bottom: 0.2rem;
    cursor: pointer;
    &:last-child {
      margin-bottom: 0;
    }
    &.is-selected,
    &:hover {
      background-color: rgba($color-white, 0.2);
    }
    &.is-empty {
      opacity: 0.5;
    }
  }
}

.tippy-box {
  background-color: $color-black;
  padding: 0;
  font-size: 1rem;
  text-align: inherit;
  color: $color-white;
  border-radius: 5px;
}

.ProseMirror {
  padding: 15px;

  li p {
    margin-bottom: 0;
  }

  pre {
    background-color: rgba(0, 0, 0, 0.05);
    color: currentColor;

    code {
      background-color: transparent !important;
    }
  }
}

.editor p.is-editor-empty:first-child::before {
  content: attr(data-empty-text);
  float: left;
  color: #aaa;
  pointer-events: none;
  height: 0;
  font-style: italic;
}

.menububble {
  position: absolute;
  display: flex;
  z-index: 20;
  background: $color-black;
  border-radius: 5px;
  padding: 0.3rem;
  margin-bottom: 0.5rem;
  transform: translateX(-50%);
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.2s, visibility 0.2s;

  &.is-active {
    opacity: 1;
    visibility: visible;
  }

  &__button {
    display: inline-flex;
    background: transparent;
    border: 0;
    color: $color-white;
    padding: 0.2rem 0.5rem;
    margin-right: 0.2rem;
    border-radius: 3px;
    cursor: pointer;

    &:last-child {
      margin-right: 0;
    }

    &:hover {
      background-color: rgba($color-white, 0.1);
    }

    &.is-active {
      background-color: rgba($color-white, 0.2);
    }
  }

  &__form {
    display: flex;
    align-items: center;
  }

  &__input {
    font: inherit;
    border: none;
    background: transparent;
    color: $color-white;
  }
}
</style>
